import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Mail, Phone, Save, UserCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function AdminProfile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      name: user?.name || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
    });
  }, [user]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put('/api/users/admin-profile', form);
      updateUser(res.data.user);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-slate-900">My Profile</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your admin account details.</p>
      </div>

      <form onSubmit={handleSubmit} className="mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-6 flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <UserCircle size={30} />
          </div>
          <div>
            <p className="text-lg font-black text-slate-900">{user?.name || 'Admin'}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{String(user?.role || 'admin').replace(/_/g, ' ')}</p>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="grid gap-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-500">Name</span>
            <input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="Admin name"
              required
            />
          </label>

          <label className="grid gap-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Mail size={14} /> Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="admin@example.com"
              required
            />
          </label>

          <label className="grid gap-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Phone size={14} /> Mobile</span>
            <input
              value={form.mobile}
              onChange={(e) => updateField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="10 digit mobile number"
            />
          </label>
        </div>

        <div className="border-t border-slate-100 p-6 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            <Save size={16} /> {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
    </div>
  );
}
